import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import coursesData from "../data/coursesData";
import { useTranslation } from 'react-i18next';

const calculatePercentage = (totalQuestions, correctAnswers) => {
    return (correctAnswers / totalQuestions) * 100;
};

export default function Exam() {
    const { t } = useTranslation();
    const { courseName, coursesClassTitle, unitName, lessonName } = useParams();
    const navigate = useNavigate();
    const formattedCourseName = courseName.replace(/-/g, ' ');
    const formattedCoursesClassTitle = coursesClassTitle.replace(/-/g, ' ');
    const formattedUnitName = unitName.replace(/-/g, ' ');
    const formattedLessonName = lessonName.replace(/-/g, ' ');

    const [isExamStarted, setIsExamStarted] = useState(false);
    const [isExamEnded, setIsExamEnded] = useState(false);
    const [selectedAnswers, setSelectedAnswers] = useState([]);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [timeLeft, setTimeLeft] = useState(null);
    const [showPopUp, setShowPopUp] = useState(false);
    const [showTimeModal, setShowTimeModal] = useState(false);
    const [hours, setHours] = useState(0);
    const [minutes, setMinutes] = useState(0);

    const questionRefs = useRef([]);

    const startExam = () => {
        const totalDuration = (hours * 3600) + (minutes * 60);
        setTimeLeft(totalDuration);
        setIsExamStarted(true);
        setShowTimeModal(false);
    };

    useEffect(() => {
        let timer;
        if (isExamStarted && timeLeft > 0) {
            timer = setInterval(() => {
                setTimeLeft(prevTime => prevTime - 1);
            }, 1000);
        }

        if (timeLeft === 0) {
            setIsExamEnded(true);
        }

        return () => clearInterval(timer);
    }, [isExamStarted, timeLeft]);

    const handleAnswerChange = (questionIndex, answer) => {
        const updatedAnswers = [...selectedAnswers];
        updatedAnswers[questionIndex] = answer;
        setSelectedAnswers(updatedAnswers);
    };

    const nextQuestion = () => {
        if (selectedAnswers[currentQuestionIndex] === undefined) {
            setShowPopUp(true);
            return;
        }

        setShowPopUp(false);

        const currentQuestionAnswers = lesson.questions[currentQuestionIndex].answers.length;
        for (let i = 0; i < currentQuestionAnswers; i++) {
            const refIndex = currentQuestionIndex * 100 + i;
            if (questionRefs.current[refIndex]) {
                questionRefs.current[refIndex].checked = false;
            }
        }
        setCurrentQuestionIndex(prevIndex => Math.min(prevIndex + 1, lesson.questions.length - 1));
    };

    const finishExam = () => {
        setIsExamEnded(true);
    };

    const reviewAnswers = () => {
        navigate(`/${courseName}/${coursesClassTitle}/${unitName}/${lessonName}/review-answers`, {
            state: { questions: lesson.questions, selectedAnswers }
        });
    };

    const course = coursesData.find(el => el.coursesName === formattedCourseName);
    if (!course) return <div>Course not found</div>;

    const courseClass = course.coursesClasses.find(cc => cc.coursesClassesTitle === formattedCoursesClassTitle);
    if (!courseClass) return <div>Course class not found</div>;

    const unit = courseClass.semesterUnits.find(u => u.semesterUnitsName === formattedUnitName);
    if (!unit) return <div>Unit not found</div>;

    const lesson = unit.lessonName.find(l => l.classesName === formattedLessonName);
    if (!lesson) return <div>Lesson not found</div>;

    const totalQuestions = lesson.questions.length;
    const correctAnswers = lesson.questions.filter((question, index) => question.passAnswers === selectedAnswers[index]).length;
    const incorrectAnswers = selectedAnswers.filter(answer => answer !== undefined && answer !== '').length - correctAnswers;
    const unansweredQuestions = totalQuestions - correctAnswers - incorrectAnswers;
    const marksMissed = (totalQuestions - correctAnswers) * 1;

    return (
        <section className="exam-page relative py-[40px]">
            <div className="container relative">
                {showTimeModal && (
                    <div className="time-modal absolute inset-0 top-[200px] flex justify-center items-center">
                        <div className="bg-white p-8 rounded-md">
                            <h2 className="text-xl font-bold mb-4">{t("Set Exam Time")}</h2>
                            <div className="flex gap-4">
                                <input type="number" placeholder="Hours" value={hours} onChange={(e) => setHours(Number(e.target.value))} className="p-2 border rounded-md" />
                                <input type="number" placeholder="Minutes" value={minutes} onChange={(e) => setMinutes(Number(e.target.value))} className="p-2 border rounded-md" />
                            </div>
                            <div className="flex justify-center gap-4 mt-4">
                                <button onClick={startExam} className="bg-[#A31D1C] text-white py-2 px-4 rounded-md">{t("Start Exam")}</button>
                                <button onClick={() => setShowTimeModal(false)} className="bg-gray-500 text-white py-2 px-4 rounded-md">{t("Cancel")}</button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="exam-stats w-full bg-white py-[15px] px-[40px] border-4 border-[#09778E] rounded-md flex items-center justify-between my-2.5">
                    <span className='text-[20px] font-bold flex items-center gap-[7px]'> {t("Number of questions :")} <span className='text-[#A31D1C]'>{lesson.questions.length}</span></span>
                    <span className='w-[2px] h-[30px] bg-black'></span>
                    <span className='text-[20px] font-bold flex items-center gap-[7px]'> {t("Exam duration :")} <span className='text-[#A31D1C]'>{`${Math.floor(timeLeft / 3600)}:${Math.floor((timeLeft % 3600) / 60)}:${timeLeft % 60}`}</span></span>
                    <span className='w-[2px] h-[30px] bg-black'></span>
                    <span className='text-[20px] font-bold flex items-center gap-[7px]'> {t("Passing score :")} <span className='text-[#A31D1C]'>50%</span></span>
                </div>

                {!isExamStarted ? (
                    <div className="exam-intro flex items-center justify-center my-[30px]">
                        <button onClick={() => setShowTimeModal(true)} className="w-fit bg-[#A31D1C] py-[10px] px-[30px] rounded-[50px] font-bold text-center capitalize mx-auto text-white">
                            {t("Start Exam")}
                        </button>
                    </div>
                ) : (
                    <div className="exam-content">
                        {isExamEnded ? (
                            <div className="exam-summary bg-white p-5 border-4 border-[#09778E] rounded-md">
                                <div className='w-[70%] mx-auto '>
                                    <h3 className="text-center text-2xl font-bold mb-4">{t("Exam Summary")}</h3>
                                    <div className="progress-bar mb-5">
                                        <div className="flex justify-between">
                                            <span className="text-[20px] font-bold">{t("Progress:")}</span>
                                            <span className="text-[#A31D1C]">{calculatePercentage(totalQuestions, correctAnswers).toFixed(2)}%</span>
                                        </div>
                                        <div className="w-full bg-gray-200 rounded-full mt-2">
                                            <div className="bg-[#09778E] text-xs font-medium text-center p-0.5 leading-none rounded-full" style={{ width: `${calculatePercentage(totalQuestions, correctAnswers)}%` }}>
                                                {calculatePercentage(totalQuestions, correctAnswers).toFixed(2)}%
                                            </div>
                                        </div>
                                    </div>
                                    <div className="exam-details mb-4">
                                        <p className='flex items-center justify-between my-[7px]'><strong>{t("Time Taken:")}</strong> {`${Math.floor(timeLeft / 3600)}:${Math.floor((timeLeft % 3600) / 60)}:${timeLeft % 60}`}</p>
                                        <p className='flex items-center justify-between my-[7px]'><strong>{t("Marks Obtained:")}</strong> {correctAnswers}/{totalQuestions}</p>
                                        <p className='flex items-center justify-between my-[7px]'><strong>{t("Correct Answers:")}</strong> {correctAnswers}</p>
                                        <p className='flex items-center justify-between my-[7px]'><strong>{t("Incorrect Answers:")}</strong> {incorrectAnswers}</p>
                                        <p className='flex items-center justify-between my-[7px]'><strong>{t("Unanswered:")}</strong> {unansweredQuestions}</p>
                                        <p className='flex items-center justify-between my-[7px]'><strong>{t("Marks Missed:")}</strong> {marksMissed}</p>
                                    </div>
                                    <div className="flex justify-between">
                                        <button onClick={reviewAnswers} className="bg-[#A31D1C] text-white py-2 px-4 rounded-md">
                                            {t("Review Answers")}
                                        </button>
                                        <Link to={"/"} onClick={finishExam} className="bg-[#09778E] text-white py-2 px-4 rounded-md">
                                            {t("Go Back")}
                                        </Link>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="questions">
                                <h2 className="text-[30px] font-bold text-[#09778E]">{formattedCourseName} - {formattedCoursesClassTitle} - {formattedUnitName} - {formattedLessonName}</h2>
                                <div className="question my-[10px] flex flex-col gap-[10px]">
                                    <div className='bg-white p-2.5 font-bold rounded-[4px] text-[19px]'>
                                        {lesson.questions[currentQuestionIndex].questionId}) {lesson.questions[currentQuestionIndex].questionsTitle}
                                    </div>
                                    <ul className='flex flex-col gap-[10px]'>
                                        {lesson.questions[currentQuestionIndex].answers.map((answer, i) => (
                                            <li key={i} className="answer-option">
                                                <label className='flex items-center gap-2.5 p-2.5 border border-[#808080] rounded-[4px]'>
                                                    <input
                                                        type="radio"
                                                        name={`question-${currentQuestionIndex}`}
                                                        value={answer}
                                                        onChange={() => handleAnswerChange(currentQuestionIndex, answer)}
                                                        ref={el => questionRefs.current[currentQuestionIndex * 100 + i] = el}
                                                    />
                                                    <div>{answer}</div>
                                                </label>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {showPopUp && (
                                    <div className="popup-message text-center bg-red-500 text-white py-2 rounded-md">
                                        {t("You must select an answer before proceeding to the next question.")}
                                    </div>
                                )}

                                {currentQuestionIndex < lesson.questions.length - 1 ? (
                                    <div className="next-question-btn mt-5">
                                        <button onClick={nextQuestion} className="w-fit bg-[#A31D1C] py-[10px] px-[30px] rounded-[50px] font-bold text-center capitalize mx-auto text-white">
                                            {t("Next")}
                                        </button>
                                    </div>
                                ) : (
                                    <div className="finish-exam-btn mt-5">
                                        <button onClick={finishExam} className="w-fit bg-[#09778E] py-[10px] px-[30px] rounded-[50px] font-bold text-center capitalize mx-auto text-white">
                                            {t("Finish Exam")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </div>
        </section>
    );
}